import {
  dispositionActionLabel,
  dispositionLabel,
} from '@/features/deal-review/deal-lead-labels'
import type { RecordDealDecision } from '@/types/api'

export type ReviewOptionTone = 'neutral' | 'positive' | 'negative' | 'warning'

export type ReviewOptionIcon =
  | 'unreviewed'
  | 'approve'
  | 'reject'
  | 'needs-information'

export interface ReviewOption<Value extends string> {
  value: Value
  label: string
  compactLabel: string
  tone: ReviewOptionTone
  icon: ReviewOptionIcon
}

export interface DecisionDraft {
  disposition: string | null
  reason: string | null
  commentary: string
  addOnConfirmed: boolean | null
}

export interface DecisionRecord {
  disposition: string
  reason: string | null
  commentary: string
  addOnConfirmed: boolean | null
}

// The form seeds from screening's recommendation, so only a deliberate edit may be saved.
export function decisionDiffers(
  record: DecisionRecord,
  draft: DecisionDraft
): boolean {
  if (draft.disposition === null) return false

  return (
    draft.disposition !== record.disposition ||
    (draft.reason ?? null) !== record.reason ||
    draft.commentary.trim() !== record.commentary.trim() ||
    draft.addOnConfirmed !== record.addOnConfirmed
  )
}

export const leadDispositionOptions = [
  {
    value: 'UNREVIEWED',
    label: 'Choose disposition',
    compactLabel: 'Choose',
    tone: 'neutral',
    icon: 'unreviewed',
  },
  {
    value: 'PURSUE',
    label: dispositionActionLabel('PURSUE'),
    compactLabel: dispositionLabel('PURSUE'),
    tone: 'positive',
    icon: 'approve',
  },
  {
    value: 'PASS',
    label: dispositionActionLabel('PASS'),
    compactLabel: dispositionLabel('PASS'),
    tone: 'negative',
    icon: 'reject',
  },
  {
    value: 'NEEDS_INFORMATION',
    label: dispositionActionLabel('NEEDS_INFORMATION'),
    compactLabel: dispositionLabel('NEEDS_INFORMATION'),
    tone: 'warning',
    icon: 'needs-information',
  },
] as const satisfies readonly ReviewOption<string>[]

// NEEDS_INFORMATION is screening's conclusion, not a reviewer choice, but the contract accepts it.
export function saveableDispositionOrNull(
  value: string
): RecordDealDecision['disposition'] | null {
  if (value === 'PURSUE' || value === 'PASS') {
    return value
  }
  return null
}

export function getLeadDispositionOption(value: string) {
  return (
    leadDispositionOptions.find((option) => option.value === value) ??
    leadDispositionOptions[0]
  )
}
